(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  function createSmokeWord(element, smokeVideo) {
    const layers = [];
    const puff = document.createElement('canvas');
    puff.width = puff.height = 48;
    const puffContext = puff.getContext('2d');
    if (!puffContext) return { update() {} };
    const gradient = puffContext.createRadialGradient(24, 24, 0, 24, 24, 24);
    gradient.addColorStop(0, '#e2e7dd');
    gradient.addColorStop(.3, '#d9e1d2a8');
    gradient.addColorStop(1, '#d9e1d200');
    puffContext.fillStyle = gradient; puffContext.fillRect(0, 0, 48, 48);
    let dispersion = 0, animated = false, phase = 0, previous = 0, frame = 0;
    const random = value => { const n = Math.sin(value * 127.1 + 311.7) * 43758.5453; return n - Math.floor(n); };

    function draw() {
      element.dataset.dispersion = dispersion.toFixed(3);
      layers.forEach(paint => paint());
    }
    function schedule() {
      if (animated && dispersion < 1 && layers.length && !frame) frame = requestAnimationFrame(tick);
      if ((!animated || dispersion >= 1) && frame) { cancelAnimationFrame(frame); frame = 0; }
    }
    function tick(now) {
      frame = 0;
      if (!animated || dispersion >= 1) return;
      if (now - previous >= 40) { phase += Math.min(now - previous, 100) / 1000; previous = now; draw(); }
      schedule();
    }
    function update(progress, canAnimate) {
      dispersion = clamp((progress - .008) / .27);
      animated = canAnimate;
      element.style.setProperty('--fallback-opacity', String(.35 * (1 - dispersion)));
      // A real wordmark at rest. As wind arrives, exchange its ink for the
      // smoke silhouette, then let the same downwind particles carry it away.
      const t = clamp(dispersion / .34), vapor = t * t * (3 - 2 * t);
      element.style.setProperty('--byline-ink', String(1 - vapor));
      element.style.setProperty('--byline-vapor', String(vapor));
      element.style.setProperty('--byline-fallback', String(1 - dispersion));
      draw(); schedule();
    }

    // Both masks share the filmed smoke, particle sprite, scroll position and animation clock.
    function addLayer(host, paintMask, area, signature = false) {
      const canvas = host.querySelector('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;
      const mask = document.createElement('canvas'), texture = document.createElement('canvas');
      mask.width = canvas.width;
      mask.height = canvas.height;
      // The glyphs stay in their original coordinates. Allow for the longest downwind
      // particle, its stretched radius and the upward curl before the blur is applied.
      const roomAbove = signature ? 144 : 192;
      const roomToRight = signature ? 216 : 960;
      canvas.width = texture.width = mask.width + roomToRight;
      canvas.height = texture.height = mask.height + roomAbove;
      canvas.style.setProperty('--smoke-width-factor', String(canvas.width / mask.width));
      canvas.style.setProperty('--smoke-top-shift', `${-100 * roomAbove / canvas.height}%`);
      const maskContext = mask.getContext('2d', { willReadFrequently: true });
      const tc = texture.getContext('2d');
      if (!maskContext || !tc) return;
      maskContext.fillStyle = '#e2e7dd'; paintMask(maskContext);
      const pixels = maskContext.getImageData(0, 0, mask.width, mask.height).data;
      const particles = [], { x0, y0, x1, y1, step } = area;
      for (let y = y0; y < y1; y += step) for (let x = x0; x < x1; x += step) {
        if (pixels[(y * mask.width + x) * 4 + 3] < 100) continue;
        const seed = random(x + y * mask.width);
        const fringe = signature && seed > .72 && pixels[((y - step * 2) * mask.width + x) * 4 + 3] < 60;
        particles.push({x:x + seed * 2, y, seed, fringe, radius:signature ? 4.5 + seed * 5.5 : 7 + seed * 5,
          alpha:signature ? .08 + seed * .11 : .08 + seed * .075});
      }
      if (!particles.length) return;
      // Feather patches of the small silhouette, where a full-strength ink fill looks flat.
      let veil;
      if (signature) {
        veil = document.createElement('canvas'); veil.width = 256; veil.height = 96;
        const vc = veil.getContext('2d'); vc.fillStyle = '#fff'; vc.fillRect(0,0,256,96);
        vc.globalCompositeOperation = 'destination-out';
        for (let i=0; i<85; i++) {
          const x=random(i+11)*256, y=random(i+113)*96, r=3+random(i+7)*13;
          const cloud=vc.createRadialGradient(x,y,0,x,y,r);
          cloud.addColorStop(0,'rgba(0,0,0,.68)'); cloud.addColorStop(1,'transparent');
          vc.fillStyle=cloud; vc.fillRect(x-r,y-r,r*2,r*2);
        }
      }
      layers.push(() => {
        context.clearRect(0, 0, canvas.width, canvas.height);
        if (dispersion >= 1 || (signature && dispersion === 0)) return;
        const solid = 1 - clamp(dispersion * (signature ? 2.6 : 3.2));
        if (solid > 0) {
          tc.globalCompositeOperation = 'source-over';
          // A lighter smoke base preserves the fine strokes of 'by' and of the small logo.
          tc.fillStyle = signature ? '#7f8b82' : '#566050';
          tc.fillRect(0, 0, mask.width, mask.height);
          if (smokeVideo.readyState >= 2) {
            if (signature) tc.globalCompositeOperation = 'screen';
            tc.globalAlpha = signature ? .88 : .8;
            tc.filter = 'grayscale(1) brightness(2)';
            tc.drawImage(smokeVideo, 0, 0, mask.width, mask.height);
            tc.filter = 'none'; tc.globalAlpha = 1;
          }
          tc.globalCompositeOperation = 'destination-in'; tc.drawImage(mask, 0, 0);
          if (veil) {
            const u=23+20*Math.sin(phase*.28), v=10+8*Math.cos(phase*.21);
            tc.drawImage(veil,u,v,205,72,0,0,mask.width,mask.height);
          }
          context.globalAlpha = solid * (signature ? .9 : .68);
          context.filter = signature ? 'blur(.75px)' : 'blur(1.4px)';
          context.drawImage(texture, 0, 0, mask.width, mask.height, 0, roomAbove, mask.width, mask.height);
          context.filter = 'none';
        }
        tc.globalCompositeOperation = 'source-over';
        tc.clearRect(0, 0, texture.width, texture.height);
        for (const particle of particles) {
          const delay = particle.x / mask.width * .2 + particle.seed * .12;
          const drift = clamp((dispersion - delay) / (1 - delay)), wind = drift * drift * (3 - 2 * drift);
          const x = particle.x + Math.sin(phase * .6 + particle.seed * 20) * 1.6
            + wind * (signature ? 200 + particle.seed * 165 : 340 + particle.seed * 370);
          const y = roomAbove + particle.y + Math.cos(phase * .45 + particle.seed * 15) * 1.4
            - wind * (35 + particle.seed * (signature ? 58 : 90)) + Math.sin(particle.seed * 20 + phase * .3 + drift * 5) * drift * 28;
          const radius = particle.radius * (1 + drift * 2.6);
          const density = .65 + .35 * Math.sin(particle.x * .022 + particle.y * .035 + phase * .5);
          tc.globalAlpha = particle.alpha * density * Math.pow(1 - drift, 1.4) / (1 + drift * 4);
          tc.drawImage(puff, x - radius, y - radius, radius * 2 * (1 + drift * 2.2), radius * 2);
          if (particle.fringe) {
            // Tiny curls rise from the actual letter edges, even before the visitor scrolls.
            const breath=.5+.5*Math.sin(phase*.72 + particle.seed*26 + particle.x*.04);
            for (let j=1; j<=3; j++) {
              const lift=j*(2.5+breath*4), spread=radius*(.85+j*.15);
              tc.globalAlpha=(.17+.14*breath)*(1-j*.19)*(1-drift);
              tc.drawImage(puff,x+lift*.85 + Math.sin(j*.9+phase*.5)*j-spread,y-lift-spread,
                spread*(2+j*.2),spread*2);
            }
          }
        }
        tc.globalAlpha = 1; context.globalAlpha = 1;
        context.filter = signature ? 'blur(.9px)' : 'blur(2px)';
        context.drawImage(texture, 0, 0); context.filter = 'none';
      });
      draw(); host.classList.add('has-smoke'); schedule();
    }
    document.fonts.ready.then(() => {
      addLayer(element.querySelector('.fumus-letters'), ctx => {
        ctx.font = '600 230px Montserrat, Arial, sans-serif';
        const letters = [...'FUMUS'], spacing = 18;
        const total = letters.reduce((sum, letter) => sum + ctx.measureText(letter).width, 0) + spacing * (letters.length - 1);
        let x = (ctx.canvas.width - total) / 2;
        letters.forEach(letter => { ctx.fillText(letter, x, 238); x += ctx.measureText(letter).width + spacing; });
      }, {x0:60,y0:30,x1:1140,y1:255,step:5});
      const byline = element.querySelector('.fumus-byline'), logo = byline.querySelector('img');
      const addSignature = () => addLayer(byline, ctx => {
        ctx.font = 'italic 62px Georgia, serif';
        ctx.textBaseline = 'middle';
        ctx.fillText('by', 40, 136);
        // Use the original wordmark's transparent silhouette, including its distinctive G.
        ctx.drawImage(logo, 136, 72, 304, 304 * logo.naturalHeight / logo.naturalWidth);
      }, {x0:36,y0:70,x1:443,y1:202,step:3}, true);
      if (logo.complete && logo.naturalWidth) addSignature();
      else logo.addEventListener('load', addSignature, {once:true});
    });
    return { update };
  }

  const story = document.querySelector('[data-smoke-story]');
  if (story) {
    const canvas = story.querySelector('[data-fumus-canvas]');
    const ctx = canvas.getContext('2d', { alpha: true });
    const stage = story.querySelector('[data-fumus-stage]');
    const poster = story.querySelector('[data-fumus-poster]');
    const smokeVideo = story.querySelector('[data-smoke-video]');
    const outletVideo = story.querySelector('[data-outlet-smoke]');
    const outletPlume = story.querySelector('[data-outlet-plume]');
    const outletAnchors = JSON.parse(story.querySelector('[data-outlet-anchors]').textContent);
    const word = story.querySelector('[data-smoke-word]');
    const smokeWord = createSmokeWord(word, smokeVideo);
    const pin = story.querySelector('.smoke-pin');
    const bar = story.querySelector('[data-smoke-progress]');
    const detail = story.querySelector('[data-fumus-detail]');
    const chapters = [...story.querySelectorAll('[data-smoke-chapter]')];
    const toggle = story.querySelector('[data-motion-toggle]');
    const urls = JSON.parse(story.querySelector('[data-fumus-frames]').textContent);
    const images = new Map();
    const pending = new Set();
    const mobile = matchMedia('(max-width: 760px)').matches;
    const stride = mobile ? 3 : 1;
    const lowData = navigator.connection?.saveData === true;
    let progress = 0, targetFrame = 0, drawnFrame = -1, visible = true, paused = false;
    let assetsStarted = false, smokeStarted = false, emissionStopped = false;

    function positionOutlet(frame = Math.max(0, drawnFrame)) {
      const anchor = outletAnchors[frame];
      const rect = canvas.getBoundingClientRect();
      const parent = pin.getBoundingClientRect();
      const scale = Math.min(rect.width / canvas.width, rect.height / canvas.height);
      const width = canvas.width * scale, height = canvas.height * scale;
      const imageLeft = rect.left - parent.left + (rect.width - width) / 2;
      const imageTop = rect.top - parent.top + (rect.height - height) / 2;
      if (!outletPlume.offsetParent) return;
      outletPlume.style.left = `${imageLeft + anchor.x * width}px`;
      outletPlume.style.top = `${imageTop + anchor.y * height}px`;
      outletPlume.style.width = `${width * .31}px`;
      outletPlume.style.height = `${width * .372}px`;
      outletPlume.style.setProperty('--outlet-angle', `${Math.atan2(anchor.dx, -anchor.dy)}rad`);
    }

    function drawFrame() {
      if (!ctx || !images.size) return;
      let frame = targetFrame;
      if (!images.has(frame)) frame = [...images.keys()].reduce((a, b) => Math.abs(a - targetFrame) < Math.abs(b - targetFrame) ? a : b);
      if (drawnFrame === frame) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(images.get(frame), 0, 0, canvas.width, canvas.height);
      drawnFrame = frame;
      canvas.dataset.frame = String(frame);
      canvas.classList.add('is-ready');
      stage.classList.add('is-ready');
      positionOutlet(frame);
    }
    function loadFrame(frame) {
      if (images.has(frame) || pending.has(frame)) return Promise.resolve();
      pending.add(frame);
      return new Promise(resolve => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => { images.set(frame, img); pending.delete(frame); drawFrame(); resolve(); };
        img.onerror = () => { pending.delete(frame); resolve(); };
        img.src = urls[frame];
      });
    }
    async function loadSequence() {
      if (assetsStarted || reduced.matches || lowData) return;
      assetsStarted = true;
      await loadFrame(0);
      const queue = [];
      for (let i = 6; i < urls.length; i += 6) queue.push(i);
      for (let i = stride; i < urls.length; i += stride) if (!queue.includes(i)) queue.push(i);
      if (!queue.includes(urls.length - 1)) queue.push(urls.length - 1);
      await Promise.all(Array.from({ length: 3 }, async () => {
        while (queue.length && !reduced.matches) await loadFrame(queue.shift());
      }));
    }
    function updateSmoke() {
      const canPlay = visible && !document.hidden && !reduced.matches && !lowData && progress < .5 && !paused;
      smokeWord.update(progress, canPlay);
      if (canPlay && !smokeStarted) {
        smokeStarted = true;
        smokeVideo.src = smokeVideo.dataset.src;
        smokeVideo.load();
        outletVideo.src = outletVideo.dataset.src;
        outletVideo.defaultPlaybackRate = 1.8;
        outletVideo.load();
        outletVideo.playbackRate = 1.8;
      }
      toggle.hidden = reduced.matches || lowData;
      if (canPlay) smokeVideo.play().catch(() => {
        toggle.textContent = toggle.dataset.play;
      });
      else smokeVideo.pause();
      if (canPlay && !emissionStopped) outletVideo.play().catch(() => {});
      else outletVideo.pause();
    }
    function updateScroll() {
      const rect = story.getBoundingClientRect();
      const top = parseFloat(getComputedStyle(pin).top) || 0;
      emissionStopped = top - rect.top > 8;
      progress = reduced.matches || lowData ? 0 : clamp((top - rect.top) / Math.max(1, rect.height - pin.offsetHeight));
      targetFrame = Math.min(urls.length - 1, Math.round(clamp((progress - .16) / .84) * (urls.length - 1) / stride) * stride);
      if (!reduced.matches && !lowData && visible) loadFrame(targetFrame);
      drawFrame();
      bar.style.transform = `scaleX(${progress})`;
      detail.classList.toggle('is-visible', progress > .36 || reduced.matches || lowData);
      chapters.forEach((chapter, index) => chapter.classList.toggle('is-active', index === (progress < .2 ? 0 : progress < .56 ? 1 : 2)));
      smokeVideo.style.opacity = String(.7 * (1 - clamp((progress - .06) / .42)));
      outletPlume.style.opacity = emissionStopped || reduced.matches || lowData ? '0' : '1';
      positionOutlet();
      stage.style.setProperty('--product-reveal', String(.8 + .2 * clamp(progress / .28)));
      poster.style.filter = progress < .2 && !reduced.matches ? 'brightness(.8)' : '';
      updateSmoke();
    }
    toggle.addEventListener('click', () => {
      paused = !paused;
      toggle.textContent = paused ? toggle.dataset.play : toggle.dataset.pause;
      toggle.setAttribute('aria-pressed', String(paused));
      updateSmoke();
    });
    let scrollQueued = false;
    window.addEventListener('scroll', () => { if (!scrollQueued) { scrollQueued = true;requestAnimationFrame(() => { scrollQueued = false;updateScroll(); }); } }, { passive: true });
    window.addEventListener('resize', updateScroll, { passive: true });
    document.addEventListener('visibilitychange', updateSmoke);
    new IntersectionObserver(entries => { visible = entries[0].isIntersecting;if (visible) { loadSequence();updateScroll(); } else updateSmoke(); }, { threshold: 0 }).observe(story);
    reduced.addEventListener('change', () => { if (reduced.matches) { targetFrame = 0;drawFrame(); } else { loadSequence(); }updateScroll(); });
    if (lowData) { story.style.height = 'auto';smokeVideo.hidden = true; }
    updateScroll();
  }

  document.querySelectorAll('[data-ritual]').forEach(section => {
    const video = section.querySelector('[data-ritual-video]');
    const toggle = section.querySelector('[data-ritual-toggle]');
    const steps = [...section.querySelectorAll('[data-ritual-step]')];
    const tag = section.querySelector('.ritual-film-tag');
    const cycle = Number(section.dataset.cycleFrames);
    const cues = section.dataset.filmCues.split(',').map(pair => pair.split(':').map(Number));
    let origin = 0, active = -1, request = 0, inView = false;
    let userPaused = reduced.matches || navigator.connection?.saveData === true;
    const setStep = index => {
      if (index === active) return;
      active = index;
      steps.forEach((step, i) => {
        step.classList.toggle('is-active', i === index);
        step.setAttribute('aria-pressed', String(i === index));
      });
      tag.textContent = 'GIAMAIO / ' + steps[index].querySelector('strong').textContent.toUpperCase();
    };
    const track = () => {
      const frame = (origin + Math.floor(video.currentTime * 30 + .001)) % cycle;
      let index = 0;
      for (const [at, chapter] of cues) { if (at > frame) break; index = chapter; }
      setStep(index);
    };
    const updateToggle = () => { toggle.textContent = video.paused ? toggle.dataset.play : toggle.dataset.pause; };
    const play = () => {
      const token = ++request;
      video.play().catch(() => { if (token === request) updateToggle(); });
    };
    // These files contain the same entire film, rotated at frame-exact chapter
    // boundaries. No remote range seek and no source swap at the next scene.
    steps.forEach((step, i) => step.addEventListener('click', () => {
      userPaused = false;
      video.pause();
      origin = Number(step.dataset.entryFrame);
      video.poster = step.dataset.ritualPoster;
      video.src = step.dataset.ritualSrc;
      video.load();
      setStep(i);
      play();
      if (matchMedia('(max-width: 760px)').matches) {
        video.scrollIntoView({behavior: reduced.matches ? 'instant' : 'smooth', block: 'center'});
      }
    }));
    video.addEventListener('timeupdate', track);
    video.addEventListener('play', updateToggle);
    video.addEventListener('pause', updateToggle);
    toggle.addEventListener('click', () => {
      userPaused = !video.paused;
      if (userPaused) video.pause(); else play();
    });
    new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (inView && !document.hidden && !userPaused) play(); else video.pause();
    }, {threshold: .2}).observe(video);
    reduced.addEventListener('change', () => {
      if (reduced.matches) { userPaused = true; video.pause(); }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) video.pause(); else if (inView && !userPaused) play();
    });
    setStep(0);
  });

  document.querySelectorAll('[data-course-preview]').forEach(frame => {
    const video = frame.querySelector('[data-course-player]');
    const launch = frame.querySelector('[data-course-launch]');
    const fallback = frame.querySelector('[data-course-fallback]');
    launch.addEventListener('click', event => {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      frame.classList.add('is-open');
      launch.hidden = true;
      video.hidden = false;
      // Make the audible play request inside the click, including on iOS.
      video.play().catch(() => { if (video.error) fallback.hidden = false; });
      video.focus({preventScroll:true});
      const box = frame.getBoundingClientRect();
      const header = document.querySelector('.site-header')?.getBoundingClientRect().bottom || 0;
      if (box.top < header || box.bottom > innerHeight) {
        frame.scrollIntoView({block:'center', behavior:reduced.matches ? 'instant' : 'smooth'});
      }
    });
    video.addEventListener('error', () => { if (!video.hidden) fallback.hidden = false; }, true);
  });

  document.querySelectorAll('[data-aroma-explorer]').forEach(explorer => {
    const tabs = [...explorer.querySelectorAll('[role="tab"]')];
    const panels = [...explorer.querySelectorAll('[role="tabpanel"]')];
    const activate = index => {
      tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index));tab.tabIndex = i === index ? 0 : -1;panels[i].hidden = i !== index; });
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', event => {
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();activate(next);tabs[next].focus();
      });
    });
  });
})();
